// DEM-45 — Engineering PI agent backend, ported from the Erez/template
// dashboard (MJOS-147) and pointed at the Engineering Dashboard MCP
// (MJOS-190). The MCP is read-only over Zeus + KB; this front-end never
// trusts the browser, scoping is enforced at the MCP server-side.
//
// This module owns the pi-agent-core Agent + its supporting plumbing:
//   - a global fetch interceptor that attaches a fresh WorkOS bearer to the
//     agent proxy routes (/cf-ai/* + /api/agent-mcp);
//   - the Llama 3.3 70B Instruct (fp8 fast) model wired to the /cf-ai/v1 proxy;
//   - the Engineering Dashboard MCP tool bridge (Zeus reads + KB);
//   - the composed system prompt (client-scoped, read-only).
import {
	Agent,
	type AgentMessage,
	type AgentTool,
	type AgentToolResult,
	convertToLlm,
} from "@earendil-works/pi-agent-core";
import { getModel } from "@earendil-works/pi-ai";
import { Type } from "typebox";
import { createMcpAgentTools } from "./mcp-bridge";
import { audioManager } from "@/components/voice/audioManager";
import { retryOn429 } from "./backoff";

export type GetAccessToken = () => Promise<string | undefined>;

// Sentinel bearer/key value — a non-empty placeholder that short-circuits
// pi-ai's Node env-var key lookup and the MCP client's auth header. The fetch
// interceptor swaps in a fresh WorkOS token before any request leaves the
// browser, so this value never actually reaches an upstream.
export const AUTH_VIA_INTERCEPTOR = "via-fetch-interceptor";

interface CachedMcpBridge {
	tools: AgentTool[];
	instructions: string;
	resources: { uri: string; name?: string; description?: string }[];
}

let cachedMcpBridge: CachedMcpBridge | null = null;

let interceptorInstalled = false;
let currentGetAccessToken: GetAccessToken = async () => undefined;
function installFetchInterceptor(getAccessToken: GetAccessToken) {
	currentGetAccessToken = getAccessToken;
	if (interceptorInstalled) return;
	interceptorInstalled = true;
	const originalFetch = globalThis.fetch.bind(globalThis);
	globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
		const urlString =
			input instanceof Request
				? input.url
				: input instanceof URL
					? input.toString()
					: String(input);
		const isAgentProxy =
			urlString.includes("/cf-ai/") ||
			urlString.includes("/api/agent-mcp") ||
			urlString.includes("/api/voice-speak");
		if (!isAgentProxy) return originalFetch(input, init);
		const token = await currentGetAccessToken();
		if (!token) {
			console.warn("[agent-panel] No WorkOS access token — proxy call will fail.");
			return originalFetch(input, init);
		}
		const headers = new Headers(
			init?.headers || (input instanceof Request ? input.headers : undefined),
		);
		headers.set("Authorization", `Bearer ${token}`);

		// One fetch attempt with the WorkOS bearer attached. For Request
		// inputs we fetch a fresh clone each attempt so the body survives a
		// retry (the original Request stays unconsumed).
		let doFetch: () => Promise<Response>;
		if (input instanceof Request) {
			const base = new Request(input, { headers });
			doFetch = () => originalFetch(base.clone(), init);
		} else {
			const retryInit = { ...init, headers };
			doFetch = () => originalFetch(input, retryInit);
		}

		// MJ-393 — 429 seatbelt for the Cloudflare Workers AI completion
		// proxy. Kimi K2.6 has a ~60 req/min per-model ceiling; a burst must
		// not kill the thread mid-answer. Bounded exponential backoff
		// (500ms -> 1s -> 2s, 3 retries) on this path only. MCP + TTS calls
		// stay single-shot.
		if (urlString.includes("/cf-ai/")) {
			return retryOn429(doFetch, {
				onRetry: ({ attempt, delayMs }) =>
					console.warn(
						`[agent-panel] cf-ai 429 - retry ${attempt + 1}/3 in ${delayMs}ms`,
					),
			});
		}
		return doFetch();
	};
}

// DEM-45 — Cloudflare Workers AI tool-using model.
// History:
//   - `@cf/meta/llama-3.3-70b-instruct-fp8-fast` — wide rate limit, but the
//     fp8 quantization degraded tool-call propensity. The model refused the
//     on-load seed with stock "your input is not sufficient" instead of
//     emitting tool_calls. Quantization eats fine-grained instruction
//     following.
//   - `@cf/openai/gpt-oss-120b` — engaged the first tool call BUT the
//     second /v1/chat/completions (with tool result) returned 400. gpt-oss
//     uses the Harmony chat format on CF; pi-agent-core's OpenAI-shape
//     tool reply structure is incompatible.
//   - `@cf/meta/llama-4-scout-17b-16e-instruct` — Llama 4 Scout MoE, marketed
//     as tool-use-first, but UNRELIABLE at native tool-calling in practice:
//     sometimes emitted a structured tool_call (→ chip), sometimes wrote the
//     call as plain TEXT (→ raw `{"name":...}` JSON leaking into the message
//     bubble). Same model, two paths = the inconsistency users saw. Out.
//   - `@cf/moonshotai/kimi-k2.6` (CURRENT — swapped back MJ-393) — the one
//     model that engaged tools cleanly and consistently. It was only ever
//     dropped for ~60 req/min rate-limit 429s under load, never for tool
//     quality. That 429 is now handled by bounded exponential backoff in the
//     fetch interceptor above, so the reason it left is gone. Tool internals
//     are additionally hidden at the render layer (final message only).
function getAgentModel() {
	const model = getModel("cloudflare-workers-ai", "@cf/moonshotai/kimi-k2.6");
	return { ...model, baseUrl: `${window.location.origin}/cf-ai/v1` };
}

async function loadMcpBridge(): Promise<CachedMcpBridge> {
	if (cachedMcpBridge) return cachedMcpBridge;
	try {
		const bridge = await createMcpAgentTools({
			url: `${window.location.origin}/api/agent-mcp`,
			bearerToken: AUTH_VIA_INTERCEPTOR,
			labelPrefix: "Engineering ",
		});
		cachedMcpBridge = {
			tools: bridge.tools,
			instructions: bridge.instructions,
			resources: bridge.resources,
		};
		console.log(
			`[agent-panel] Engineering MCP connected — ${bridge.tools.length} tools, ${bridge.resources.length} resources, instructions ${bridge.instructions.length} chars.`,
		);
		return cachedMcpBridge;
	} catch (err) {
		console.error("[agent-panel] Failed to load MCP bridge:", err);
		return { tools: [], instructions: "", resources: [] };
	}
}

// `speak` — client-side AgentTool that vocalizes a short line through the
// dashboard's TTS pipeline. Renders as a tool chip in the assistant-ui
// Thread (same chip UX as the Zeus reads) and plays audio inline via the
// shared audioManager. No R2, no DB, no broadcast — see
// functions/api/voice-speak.ts for the WorkOS-authed TTS proxy. The fetch
// interceptor adds the WorkOS bearer.
function buildSpeakTool(): AgentTool {
	return {
		name: "speak",
		label: "speak",
		description:
			"Vocalize a short remark aloud in the dashboard (1-2 sentences max). " +
			"Use SPARINGLY — only when a brief spoken summary genuinely helps the " +
			"user (e.g. read the headline status, flag a notable anomaly). Never " +
			"speak the whole reply — write the full content in chat, and pick at " +
			"most one or two sentences for voice. English only (Kokoro v1 has no " +
			"Hebrew model). Returns when audio playback has been kicked off.",
		parameters: Type.Object({
			text: Type.String({
				description: "The English line to read aloud. Keep under ~200 characters.",
				maxLength: 1500,
			}),
		}),
		execute: async (_toolCallId: string, params: any): Promise<AgentToolResult<any>> => {
			const text = String(params?.text ?? "").trim();
			if (!text) {
				return {
					content: [{ type: "text", text: "speak: text is required." }],
					details: { error: "missing_text" },
				};
			}
			try {
				const resp = await fetch(`${window.location.origin}/api/voice-speak`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ text }),
				});
				if (!resp.ok) {
					const detail = await resp.text().catch(() => "");
					return {
						content: [
							{
								type: "text",
								text: `speak failed: ${resp.status} ${detail.slice(0, 200)}`,
							},
						],
						details: { error: "tts_failed", status: resp.status },
					};
				}
				const blob = await resp.blob();
				const url = URL.createObjectURL(blob);
				audioManager.play(url);
				return {
					content: [
						{
							type: "text",
							text: `Spoke aloud: "${text.slice(0, 200)}"${text.length > 200 ? "…" : ""}`,
						},
					],
					details: { chars: text.length },
				};
			} catch (err) {
				return {
					content: [
						{
							type: "text",
							text: `speak error: ${err instanceof Error ? err.message : String(err)}`,
						},
					],
					details: { error: String(err) },
				};
			}
		},
	};
}

function composeSystemPrompt(bridge: CachedMcpBridge, userEmail?: string): string {
	const youFor = userEmail ? `for ${userEmail}` : "for this client";
	const preamble = `You are the Engineering Dashboard PI agent ${youFor} — a read-only assistant for live Zeus water-operations telemetry.

GROUND RULES
- You are scoped to THIS client's stations. The MCP server enforces this — if a tool refuses an out-of-scope station you accept the refusal and explain it to the user. Never try to bypass it.
- You are READ-ONLY. You have no Zeus write / pump-control / configuration tools. If the user asks you to change something on the system, tell them you cannot and recommend they contact the engineer.
- Always ground claims in tool output. Numbers come from Zeus reads (current flow, pressure, DO2, temperature, daily volumes, alarm history). When data is missing, say so — never fabricate a value.

WHAT YOU SEE
- The client has access to their station's live readings, recent history, and any alarms/events on Zeus.
- The dashboard surface itself shows live values + 24h / 7d / 30d trends — your job is to be the conversational partner around it.

PROACTIVE ON LOAD — STRICT
- The first user message is the on-load seed. Do NOT ask for clarification. Do NOT say "your input is not sufficient." Treat it as an unambiguous order to read live data.
- MANDATORY tool sequence on the first turn: (1) call \`list_my_stations\` to discover the client's station IMEI, (2) call \`zeus\` with \`method: "GetLastKnownValuesExtended"\` and that IMEI to read live channels, (3) optionally call \`zeus\` with \`method: "GetAlarm"\` for active alarms.
- Then post a short status read: "all nominal" OR, if something looks off, name exactly which channel is off (channel name, value, unit). End with 1-2 short follow-up questions the user is most likely to want to ask next ("want me to compare this week to last?", "want the 24h alarm history?").

VOICE (\`speak\` tool)
- You have a \`speak\` tool that vocalizes a short remark through the dashboard's TTS. Use it SPARINGLY: at most once per turn, and ONLY when a brief spoken cue genuinely helps (a one-line headline status on the proactive sweep, or a callout when you find a real anomaly). Never narrate your whole reply. English only. Always write the full content in chat — \`speak\` is a one-sentence accent, not the message body.

You are running on Kimi K2.6 via Cloudflare Workers AI. Be direct, concise, and honest.`;

	const parts = [preamble];
	if (bridge.instructions) {
		parts.push(
			`\n<mcp_server_instructions source="engineering-dashboard-mcp">\n${bridge.instructions}\n</mcp_server_instructions>`,
		);
	}
	if (bridge.resources.length > 0) {
		const catalog = bridge.resources
			.map((r) => `- ${r.uri}${r.description ? ` — ${r.description}` : ""}`)
			.join("\n");
		parts.push(
			`\n<mcp_resources count="${bridge.resources.length}">\nAvailable resources (fetch any with \`read_mcp_resource\`):\n${catalog}\n</mcp_resources>`,
		);
	}
	return parts.join("\n");
}

export interface CreateEngineeringAgentOptions {
	getAccessToken: GetAccessToken;
	userEmail?: string;
}

function ensureProcessShim() {
	const g = globalThis as unknown as { process?: { env?: Record<string, string> } };
	if (typeof g.process === "undefined") g.process = { env: {} };
	else if (!g.process.env) g.process.env = {};
}

export async function createEngineeringAgent(
	opts: CreateEngineeringAgentOptions,
): Promise<Agent> {
	ensureProcessShim();
	installFetchInterceptor(opts.getAccessToken);
	const bridge = await loadMcpBridge();
	const agent = new Agent({
		initialState: {
			systemPrompt: composeSystemPrompt(bridge, opts.userEmail),
			model: getAgentModel(),
			thinkingLevel: "off",
			messages: [] as AgentMessage[],
			tools: [...bridge.tools, buildSpeakTool()],
		},
		convertToLlm,
		getApiKey: () => AUTH_VIA_INTERCEPTOR,
	});
	if (import.meta.env.DEV) {
		(globalThis as { __engineeringAgent?: Agent }).__engineeringAgent = agent;
	}
	return agent;
}
